import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type LegalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: React.ReactNode;
};
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: LegalModalProps) {
  return <AnimatePresence data-id="element-11">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50" data-id="element-12" />

          {/* Modal Content */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-13">
            <div className="bg-white w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl flex flex-col pointer-events-auto overflow-hidden" data-id="element-14">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-cream-dark bg-cream/30" data-id="element-15">
                <h3 className="text-xl font-serif font-bold text-charcoal" data-id="element-16">
                  {title}
                </h3>
                <button onClick={onClose} className="p-2 text-charcoal-light hover:text-terracotta hover:bg-terracotta/10 rounded-full transition-colors" aria-label="Cerrar" data-id="element-17">
                  <X size={20} data-id="element-18" />
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="p-6 md:p-8 overflow-y-auto text-charcoal-light space-y-4 text-sm leading-relaxed" data-id="element-19">
                {content}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<'privacy' | 'terms' | null>(null);
  const openModal = (type: 'privacy' | 'terms') => {
    setModalContent(type);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => setModalContent(null), 300); // Clear content after animation
  };
  const privacyContent = <>
      <p data-id="element-20">
        <strong data-id="element-21">Última actualización: {new Date().getFullYear()}</strong>
      </p>
      <p data-id="element-22">
        En "Plan 21 Días Alta Proteína", respetamos su privacidad y estamos
        comprometidos a protegerla mediante el cumplimiento de esta política.
      </p>
      <h4 className="font-bold text-charcoal mt-4" data-id="element-23">
        1. Recopilación de información
      </h4>
      <p data-id="element-24">
        Recopilamos información que usted nos proporciona directamente cuando
        realiza una compra, como su nombre y dirección de correo electrónico,
        únicamente para procesar su pedido y enviarle el producto digital.
      </p>
      <h4 className="font-bold text-charcoal mt-4" data-id="element-25">2. Uso de la información</h4>
      <p data-id="element-26">
        Utilizamos la información recopilada para entregarle el producto digital
        adquirido y enviarle actualizaciones relacionadas con el mismo. No
        compartimos su información con terceros con fines comerciales.
      </p>
      <h4 className="font-bold text-charcoal mt-4" data-id="element-27">
        3. Seguridad de los datos
      </h4>
      <p data-id="element-28">
        Implementamos medidas de seguridad diseñadas para proteger su
        información personal contra pérdidas accidentales y contra el acceso,
        uso, alteración y divulgación no autorizados.
      </p>
      <h4 className="font-bold text-charcoal mt-4" data-id="element-29">4. Sus derechos</h4>
      <p data-id="element-30">
        Usted tiene derecho a solicitar el acceso, corrección o eliminación de
        sus datos personales en cualquier momento contactándonos directamente.
      </p>
    </>;
  const termsContent = <>
      <p data-id="element-31">
        <strong data-id="element-32">Última actualización: {new Date().getFullYear()}</strong>
      </p>
      <p data-id="element-33">
        Al comprar y descargar el "Plan 21 Días Alta Proteína", usted acepta los
        siguientes términos y condiciones.
      </p>
      <h4 className="font-bold text-charcoal mt-4" data-id="element-34">1. Propiedad Intelectual</h4>
      <p data-id="element-35">
        Todo el contenido incluido en este producto digital (textos, gráficos,
        logotipos, imágenes y compilaciones de datos) es propiedad de "Plan 21
        Días Alta Proteína" y está protegido por las leyes de derechos de autor.
      </p>
      <h4 className="font-bold text-charcoal mt-4" data-id="element-36">2. Licencia de Uso</h4>
      <p data-id="element-37">
        Se le otorga una licencia limitada, no exclusiva e intransferible para
        acceder y hacer uso personal y no comercial de este producto digital. No
        está permitido revender, distribuir o compartir este material con
        terceros.
      </p>
      <h4 className="font-bold text-charcoal mt-4" data-id="element-38">3. Política de Reembolso</h4>
      <p data-id="element-39">
        Ofrecemos una garantía de satisfacción de 7 días. Si no está satisfecho
        con el producto, puede solicitar un reembolso completo dentro de los 7
        días posteriores a su compra.
      </p>
      <h4 className="font-bold text-charcoal mt-4" data-id="element-40">
        4. Descargo de Responsabilidad
      </h4>
      <p data-id="element-41">
        Este plan es una guía nutricional general y no sustituye el consejo
        médico profesional. Consulte a su médico antes de comenzar cualquier
        cambio significativo en su dieta.
      </p>
    </>;
  return <>
      <footer className="text-cream py-12 px-4 border-t border-charcoal-light/20 bg-[#F2F1EE]" data-id="element-42">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-43">
          <div className="text-center md:text-left" data-id="element-44">
            <p className="font-serif font-bold mb-1 text-[20px] text-[#DF1189]" data-id="element-45">
              Plan 21 Días Alta Proteína
            </p>
            <p className="text-charcoal-light text-[14px] font-[500]" data-id="element-46">
              © 2026 Todos los derechos reservados. Este producto no sustituye
              el consejo médico profesional.
            </p>
          </div>

          <div className="flex gap-6 text-sm font-medium" data-id="element-47">
            <button onClick={() => openModal('privacy')} className="text-charcoal-light hover:text-[#DF1189] transition-colors underline decoration-transparent hover:decoration-[#DF1189] underline-offset-4" data-id="element-48">
              Política de Privacidad
            </button>
            <button onClick={() => openModal('terms')} className="text-charcoal-light hover:text-[#DF1189] transition-colors underline decoration-transparent hover:decoration-[#DF1189] underline-offset-4" data-id="element-49">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={modalOpen} onClose={closeModal} title={modalContent === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'} content={modalContent === 'privacy' ? privacyContent : termsContent} data-id="element-50" />
    </>;
}